import { useState } from 'react';
import {
  Stack,
  Title,
  Card,
  Text,
  Group,
  List,
  Skeleton,
  Alert,
  Divider,
  Grid,
  ThemeIcon,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSparkles, IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useBillingPlan } from '../../hooks/useBilling';
import { useUpgradeTrial } from '../../hooks/useBillingMutations';
import { AccountUtils } from '../../AccountUtils';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { GoogleAds } from '@/shared/utils/googleAds';
import { PaymentMethodForm } from './PaymentMethodForm';
import type { PaymentMethodFormValues } from '../../schemas/billingSchemas';
import type { IBillingAccountViewData } from '@/lib/types/Billing';

/**
 * Trial → paid upgrade as a single-plan checkout: order summary + payment form.
 * Deliberately NOT a current-vs-upgrade comparison — the upgrade target is the
 * same plan minus the trial, so a comparison shows two identical feature lists
 * and reads as "the money buys nothing new". Revisit as a plan picker if
 * multiple paid tiers are ever offered.
 */
export function TrialUpgradePage() {
  const navigate = useNavigate();
  const { billingPlan, isLoading } = useBillingPlan();
  const upgradeMutation = useUpgradeTrial();
  const { customerInfo, authData } = useAuth();
  const [authError, setAuthError] = useState<string | null>(null);

  const parentCId = AccountUtils.resolveParentCId(customerInfo, authData);

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Title order={2}>Upgrade Your Plan</Title>
        <Skeleton height={200} radius="md" />
        <Skeleton height={300} radius="md" />
      </Stack>
    );
  }

  if (!billingPlan) {
    return (
      <Stack gap="lg">
        <Title order={2}>Upgrade Your Plan</Title>
        <Text c="dimmed">Unable to load plan information.</Text>
      </Stack>
    );
  }

  const currentPlan = billingPlan.billingPlan;
  const upgradePlan = billingPlan.recommendedUpgradePlan;
  const currentLabel = currentPlan.publicLabel || currentPlan.name;
  const upgradeLabel = upgradePlan ? upgradePlan.publicLabel || upgradePlan.name : '';

  const daysRemaining = AccountUtils.trialDaysRemaining(
    billingPlan.expires,
    Math.floor(Date.now() / 1000),
  );
  const urgencyColor = AccountUtils.trialUrgencyColor(daysRemaining);

  // Build empty billing data for a new payment method form
  const emptyPaymentMethod: IBillingAccountViewData = {
    billingAccountId: '',
    billingAccountType: 'card',
    fullName: '',
    orgName: null,
    address1: '',
    city: '',
    region: '',
    postalCode: '',
    countryIso2: 'US',
    cardDetails: null,
    created: 0,
    updated: 0,
  };

  async function handleSubmit(values: PaymentMethodFormValues) {
    if (!parentCId || !upgradePlan) return;
    setAuthError(null);

    const billingData: IBillingAccountViewData = {
      ...emptyPaymentMethod,
      fullName: values.fullName,
      orgName: values.orgName || null,
      address1: values.address1,
      city: values.city,
      region: values.region,
      postalCode: values.postalCode,
      countryIso2: values.countryIso2,
      cardDetails: {
        cardNum: values.cardNum,
        cardLastFour: values.cardNum.slice(-4),
        cardType: 'visa',
        cardMonth: values.cardMonth,
        cardYear: values.cardYear,
        cvv: values.cvv,
      },
    };

    try {
      const result = await upgradeMutation.mutateAsync({
        cId: parentCId,
        billing: billingData,
        planId: upgradePlan.id,
      });

      if (result.authResult && result.authResult.responseCodeName !== 'approved') {
        setAuthError(result.authResult.reason || 'Payment was declined. Please check your card details.');
        return;
      }

      // Premium plans are keyed by "no stream limit" (legacy conversion-snippet parity)
      GoogleAds.trackTrialUpgradeConversion(upgradePlan.limitStreams == null);

      notifications.show({
        title: 'Upgrade Successful',
        message: `You have been upgraded to the ${upgradePlan.publicLabel || upgradePlan.name} plan.`,
        color: 'green',
      });
      navigate({ to: '/account/usage' });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to upgrade plan.',
        color: 'red',
      });
    }
  }

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Title order={2}>Upgrade Your Plan</Title>
        <Text size="sm" fw={500} c={urgencyColor === 'blue' ? 'dimmed' : urgencyColor}>
          {AccountUtils.trialStatusMessage(daysRemaining)}
        </Text>
      </Stack>

      {upgradePlan ? (
        <>
          <Text size="sm" c="dimmed">
            You're on the {currentLabel} plan. Upgrading keeps everything exactly as it is —
            your videos, streams, players, and settings carry over.
          </Text>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Grid gutter="xl">
              <Grid.Col span={{ base: 12, md: 5 }}>
                <Stack gap="sm">
                  <Text size="sm" c="dimmed" tt="uppercase" fw={500}>
                    Your Plan
                  </Text>
                  <Text fw={700} size="xl">{upgradeLabel}</Text>
                  <Text fw={600} size="lg" c="blue">
                    ${upgradePlan.pricePerMonth.toFixed(2)}
                    <Text span size="sm" c="dimmed">/month</Text>
                  </Text>
                  <Divider />
                  <Stack gap={4}>
                    <Text size="sm">Storage: {upgradePlan.limitStorageGB} GB</Text>
                    <Text size="sm">Transfer: {upgradePlan.limitTransferGB} GB</Text>
                  </Stack>
                  {upgradePlan.features.length > 0 && (
                    <List
                      size="sm"
                      spacing={6}
                      center
                      icon={
                        <ThemeIcon color="green" variant="light" size={18} radius="xl">
                          <IconCheck size={12} />
                        </ThemeIcon>
                      }
                    >
                      {upgradePlan.features.map((f, i) => (
                        <List.Item key={i}>{f}</List.Item>
                      ))}
                    </List>
                  )}
                </Stack>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 7 }}>
                <Stack gap="md">
                  <Group gap="sm">
                    <IconSparkles size={20} />
                    <Text fw={600} size="lg">Payment Details</Text>
                  </Group>
                  <Text size="sm" c="dimmed">
                    You will be charged ${upgradePlan.pricePerMonth.toFixed(2)}/month for the{' '}
                    {upgradeLabel} plan.
                  </Text>
                  <PaymentMethodForm
                    paymentMethod={emptyPaymentMethod}
                    onSubmit={handleSubmit}
                    onCancel={() => navigate({ to: '/account/usage' })}
                    isPending={upgradeMutation.isPending}
                    authError={authError}
                    submitLabel="Upgrade Now"
                  />
                </Stack>
              </Grid.Col>
            </Grid>
          </Card>
        </>
      ) : (
        <Alert icon={<IconAlertCircle size={16} />} color="yellow">
          No upgrade plan is available at this time. Please contact support.
        </Alert>
      )}
    </Stack>
  );
}
